export type PurchasePlan = "pro" | "unknown";

export type PurchasePackage = {
  identifier: string;
  title: string;
  priceText: string;
  plan: PurchasePlan;
  billingPeriod: "monthly" | "annual" | "other";
  raw: unknown;
};

export type PurchaseCustomerInfo = {
  activeEntitlements: string[];
  hasPro: boolean;
  managementUrl: string | null;
  originalAppUserId: string | null;
};

export type PaywallPresentationResult =
  "purchased" | "restored" | "cancelled" | "not_presented" | "error";

export type PurchaseState = {
  configured: boolean;
  loading: boolean;
  purchasing: boolean;
  managing: boolean;
  packages: PurchasePackage[];
  customerInfo: PurchaseCustomerInfo | null;
  hasPro: boolean;
  error: string | null;
};

export type CustomerInfoListener = (info: PurchaseCustomerInfo) => void;

export type PurchaseService = {
  load(userId: string): Promise<{
    configured: boolean;
    packages: PurchasePackage[];
    customerInfo: PurchaseCustomerInfo;
  }>;
  purchase(pkg: PurchasePackage): Promise<PurchaseCustomerInfo>;
  restore(): Promise<PurchaseCustomerInfo>;
  getCustomerInfo(): Promise<PurchaseCustomerInfo>;
  getManagementUrl(): Promise<string | null>;
  presentPaywall(): Promise<PaywallPresentationResult>;
  presentPaywallIfNeeded(): Promise<PaywallPresentationResult>;
  presentCustomerCenter(): Promise<void>;
  addCustomerInfoListener(listener: CustomerInfoListener): () => void;
  supportsNativePaywall: boolean;
  supportsCustomerCenter: boolean;
};

export function inferPurchasePlan(...values: (string | null | undefined)[]) {
  const haystack = values.filter(Boolean).join(" ").toLowerCase();
  if (
    haystack.includes("florafolio") ||
    haystack.includes("pro") ||
    haystack.includes("monthly") ||
    haystack.includes("$rc_monthly")
  ) {
    return "pro";
  }
  return "unknown";
}

export function purchasePlanRank(plan: PurchasePlan) {
  return plan === "pro" ? 0 : 1;
}

export function purchaseBillingRank(
  billingPeriod: PurchasePackage["billingPeriod"],
) {
  if (billingPeriod === "monthly") return 0;
  if (billingPeriod === "annual") return 1;
  return 2;
}

export function purchaseErrorMessage(error: unknown, fallback: string) {
  if (
    error &&
    typeof error === "object" &&
    "userCancelled" in error &&
    (error as { userCancelled?: boolean }).userCancelled
  ) {
    return null;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}
